/**
 * The client ground surface (AD-408 §3) — `groundHeightAt(x, z)` and the
 * heightfield it is the vertex function of. Pure: no `three`, no I/O, so the
 * parity/coplanarity/byte-stability pins run under `node --test`.
 *
 * - **Grid.** Vertices at pixel CENTRES of the bound tile set, for every global
 *   pixel whose centre lies inside the zone bounds expanded by one pixel on each
 *   side (so the mesh always covers the bounds' edges). Vertex height = the
 *   raster's int16 elevation read through the SHARED sampling law
 *   (`createTerrainLayerReader`), minus the datum. A padded vertex the set does
 *   not cover takes the nearest covered vertex's height — the ring never invents
 *   relief.
 * - **Triangles.** Each cell splits on the FIXED diagonal (i, j)–(i+1, j+1);
 *   `heightAt` barycentrically interpolates the triangle containing the point, so
 *   the seat and the mesh are coplanar by construction — one function feeds both.
 * - **Datum.** `y_visual = h(x, z) − h(spawn)`: the player stands at y ≈ 0 where
 *   they arrive. The datum is quantised to 1/1024 m (sub-millimetre) so that
 *   `raw − datum` is exact in float64 AND float32 — which is what makes the pixel
 *   parity an exact integer equality and the GPU vertex buffer lossless.
 * - **Vertical scale 1.0, no knob.** Metres are metres (AD-146's frozen mapping).
 *
 * ⛔ Presentation only. Gameplay stays on the flat plane: movement, prediction,
 * collision and placement never read this (AD-146 header; AD-408 §4). Nothing is
 * on the wire. Built once per zone join (off the per-tick path); `heightAt` is a
 * floor, two compares and a handful of multiplies per call.
 */

import type {
  ClientGenerationSpec,
  CollisionShape,
  TerrainLayerSet,
  ZoneBounds,
} from '@great-cloud/shared/client';
import {
  createTerrainLayerReader,
  settlementClearings,
  settlementPiecePlacements,
  terrainTileRange,
} from '@great-cloud/shared/client';

/** The render-only heightfield a zone's ground mesh is built from (plain data). */
export interface HeightfieldPlan {
  /** Bound tile set id (diagnostics). */
  setId: string;
  /** World x of vertex column 0 / z of vertex row 0 (a pixel centre). */
  originX: number;
  originZ: number;
  /** Vertex spacing in metres (== the set's metersPerPixel). */
  spacing: number;
  /** Vertex counts (≥ 3 each, thanks to the one-pixel padding). */
  cols: number;
  rows: number;
  /** Row-major vertex heights, datum-subtracted (metres). Length cols·rows. */
  heights: number[];
  /** The datum subtracted from every raw elevation (metres, 1/1024-quantised). */
  datum: number;
}

/** The ground a zone's drawn objects are seated on. */
export interface GroundSurface {
  /** Visual ground height at a world point (0 everywhere on flat ground). */
  heightAt(x: number, z: number): number;
  /** The heightfield behind `heightAt`, or null for the flat AD-315 ground. */
  heightfield: HeightfieldPlan | null;
}

/** Flat ground: today's AD-315 behaviour, byte for byte. */
export const FLAT_GROUND: GroundSurface = { heightAt: () => 0, heightfield: null };

/** Datum quantum: 1/1024 m keeps `int16 − datum` exact in float32 up to ±16 km. */
const DATUM_QUANTUM = 1024;

/**
 * Evaluate the fixed-diagonal piecewise-linear surface of a heightfield at a
 * world point (outside the grid the edge cell is extended by clamping, so a
 * point on the padded ring's outer half still gets a defined, bounded height).
 */
export function heightfieldHeightAt(hf: HeightfieldPlan, x: number, z: number): number {
  const u = (x - hf.originX) / hf.spacing;
  const v = (z - hf.originZ) / hf.spacing;
  const i = Math.min(hf.cols - 2, Math.max(0, Math.floor(u)));
  const j = Math.min(hf.rows - 2, Math.max(0, Math.floor(v)));
  const fu = Math.min(1, Math.max(0, u - i));
  const fv = Math.min(1, Math.max(0, v - j));
  const h = hf.heights;
  const c = hf.cols;
  const h00 = h[j * c + i] as number;
  const h11 = h[(j + 1) * c + i + 1] as number;
  if (fu >= fv) {
    const h10 = h[j * c + i + 1] as number;
    return h00 + fu * (h10 - h00) + fv * (h11 - h10);
  }
  const h01 = h[(j + 1) * c + i] as number;
  return h00 + fv * (h01 - h00) + fu * (h11 - h01);
}

/** A circle of ground that must be drawn level (a settlement piece, its collider, its yard). */
export interface LevelPad {
  x: number;
  z: number;
  radius: number;
}

/**
 * The level-pad radius around a settlement piece's origin (q-terrain-level-pads).
 * Kit pieces are authored on flat ground (AD-044 base-at-y=0); this covers a
 * bench, rack or small house. A larger piece also contributes its collider's
 * bounding circle, so a hall's whole footprint is levelled.
 */
export const PIECE_PAD_RADIUS_M = 4;

/** The bounding circle of a collision footprint. */
function shapeCircle(shape: CollisionShape): LevelPad {
  if (shape.kind === 'circle') return { x: shape.x, z: shape.z, radius: shape.radius };
  const xs = shape.kind === 'aabb' ? [shape.minX, shape.maxX] : shape.points.map((p) => p.x);
  const zs = shape.kind === 'aabb' ? [shape.minZ, shape.maxZ] : shape.points.map((p) => p.z);
  const x = (Math.min(...xs) + Math.max(...xs)) / 2;
  const z = (Math.min(...zs) + Math.max(...zs)) / 2;
  const radius = Math.hypot(Math.max(...xs) - x, Math.max(...zs) - z);
  return { x, z, radius };
}

/**
 * The ground a zone's settlements stand on, as level pads (q-terrain-level-pads,
 * owner 2026-10-31: "levelled underneath items like houses and benches so they
 * stand level and flat on the ground"). Every drawn settlement piece (the SHARED
 * placement law), every descriptor collider that touches one (its real
 * footprint), and every authored settlement yard (`settlementClearings`). Wild
 * colliders away from a settlement are not pads: relief stays outside towns.
 */
export function settlementLevelPads(
  generation: Pick<ClientGenerationSpec, 'settlements'> | null | undefined,
  collision: readonly CollisionShape[] = [],
): LevelPad[] {
  const pieces: LevelPad[] = settlementPiecePlacements(generation?.settlements).map((p) => ({
    x: p.x,
    z: p.z,
    radius: PIECE_PAD_RADIUS_M,
  }));
  const pads = [...pieces];
  for (const shape of collision) {
    const c = shapeCircle(shape);
    if (pieces.some((p) => Math.hypot(p.x - c.x, p.z - c.z) <= p.radius + c.radius)) pads.push(c);
  }
  pads.push(...settlementClearings(generation?.settlements));
  return pads;
}

/**
 * Level a heightfield under a set of pads. The surface is piecewise-linear on
 * the vertex grid, so a pad is level only when EVERY vertex of every cell it
 * touches shares one height: each pad claims those vertices, pads that share a
 * vertex merge into one cluster, and each cluster is set to the mean of its
 * vertices' heights (the least earth moved). Outside the claimed cells the
 * relief is untouched; the cells around a cluster carry the slope.
 */
export function levelHeightfield(hf: HeightfieldPlan, pads: readonly LevelPad[]): HeightfieldPlan {
  if (pads.length === 0) return hf;
  const { cols, rows, spacing: s } = hf;
  const cell = (v: number, origin: number, max: number): number =>
    Math.min(max, Math.max(0, Math.floor((v - origin) / s)));
  const parent = pads.map((_, k) => k);
  const find = (k: number): number => {
    while (parent[k] !== k) k = parent[k] = parent[parent[k] as number] as number;
    return k;
  };
  const owner = new Map<number, number>();
  pads.forEach((pad, k) => {
    const i0 = cell(pad.x - pad.radius, hf.originX, cols - 2);
    const i1 = cell(pad.x + pad.radius, hf.originX, cols - 2);
    const j0 = cell(pad.z - pad.radius, hf.originZ, rows - 2);
    const j1 = cell(pad.z + pad.radius, hf.originZ, rows - 2);
    for (let j = j0; j <= j1 + 1; j += 1) {
      for (let i = i0; i <= i1 + 1; i += 1) {
        const v = j * cols + i;
        const other = owner.get(v);
        if (other === undefined) owner.set(v, k);
        else parent[find(other)] = find(k);
      }
    }
  });
  const clusters = new Map<number, number[]>();
  for (const v of [...owner.keys()].sort((a, b) => a - b)) {
    const root = find(owner.get(v) as number);
    const members = clusters.get(root) ?? [];
    members.push(v);
    clusters.set(root, members);
  }
  const heights = hf.heights.slice();
  const levelled: number[] = [];
  for (const members of clusters.values()) {
    let sum = 0;
    for (const v of members) sum += hf.heights[v] as number;
    const level = sum / members.length;
    for (const v of members) {
      heights[v] = level;
      levelled.push(v);
    }
  }
  // Ramp: a vertex within LEVEL_FEATHER_CELLS of a pad moves toward the nearest
  // pad's level, so the plateau eases into the relief instead of a one-cell cliff.
  levelled.sort((a, b) => a - b);
  for (let v = 0; v < heights.length; v += 1) {
    if (owner.has(v)) continue;
    const i = v % cols;
    const j = Math.floor(v / cols);
    let nearest = -1;
    let bestD = Infinity;
    for (const u of levelled) {
      const d = Math.hypot((u % cols) - i, Math.floor(u / cols) - j);
      if (d < bestD) {
        bestD = d;
        nearest = u;
      }
    }
    if (bestD > LEVEL_FEATHER_CELLS) continue;
    const level = heights[nearest] as number;
    heights[v] = level + ((hf.heights[v] as number) - level) * (bestD / (LEVEL_FEATHER_CELLS + 1));
  }
  return { ...hf, heights };
}

/** Vertex rings (grid cells) over which a levelled pad eases back into the relief. */
export const LEVEL_FEATHER_CELLS = 2;

/**
 * Build the ground surface for a zone. Returns {@link FLAT_GROUND} when no set is
 * bound, the bounds are degenerate, or the set does not cover every tile the
 * bounds span (AD-408 §0(c): partial coverage never ships a half-relief ground).
 *
 * @param datumPoint the joining client's authoritative spawn (`helloAck.you`).
 * @param pads ground drawn level under the zone's settlements ({@link settlementLevelPads});
 *   levelled before the datum, so a spawn on a pad stands at y = 0.
 */
export function buildGroundSurface(
  set: TerrainLayerSet | null,
  bounds: ZoneBounds,
  datumPoint: { x: number; z: number },
  pads: readonly LevelPad[] = [],
): GroundSurface {
  if (set === null) return FLAT_GROUND;
  if (!(bounds.maxX > bounds.minX) || !(bounds.maxZ > bounds.minZ)) return FLAT_GROUND;
  const m = set.metersPerPixel;
  const have = new Set(set.tiles.map((t) => `${t.tx},${t.tz}`));
  const range = terrainTileRange(bounds, set);
  for (let tz = range.tzMin; tz <= range.tzMax; tz += 1) {
    for (let tx = range.txMin; tx <= range.txMax; tx += 1) {
      if (!have.has(`${tx},${tz}`)) return FLAT_GROUND;
    }
  }

  // Global pixel range whose CENTRES lie inside the bounds expanded by one pixel.
  const gpxMin = Math.ceil((bounds.minX - m - set.origin.x) / m - 0.5);
  const gpxMax = Math.floor((bounds.maxX + m - set.origin.x) / m - 0.5);
  const gpzMin = Math.ceil((bounds.minZ - m - set.origin.z) / m - 0.5);
  const gpzMax = Math.floor((bounds.maxZ + m - set.origin.z) / m - 0.5);
  const cols = gpxMax - gpxMin + 1;
  const rows = gpzMax - gpzMin + 1;
  if (cols < 2 || rows < 2) return FLAT_GROUND;

  const reader = createTerrainLayerReader(set, 'elevation');
  const raw: (number | null)[] = new Array<number | null>(cols * rows);
  const covered: number[] = [];
  for (let j = 0; j < rows; j += 1) {
    for (let i = 0; i < cols; i += 1) {
      const value = reader.valueAtPixel(gpxMin + i, gpzMin + j);
      raw[j * cols + i] = value;
      if (value !== null) covered.push(j * cols + i);
    }
  }
  if (covered.length === 0) return FLAT_GROUND;
  const filled: number[] = raw.map((value, index) => {
    if (value !== null) return value;
    // Nearest covered vertex (squared grid distance; ties → lowest index, deterministic).
    const i = index % cols;
    const j = Math.floor(index / cols);
    let best = covered[0] as number;
    let bestD = Infinity;
    for (const k of covered) {
      const d = ((k % cols) - i) ** 2 + (Math.floor(k / cols) - j) ** 2;
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
    return raw[best] as number;
  });

  const rawPlan = levelHeightfield(
    {
      setId: set.id,
      originX: set.origin.x + (gpxMin + 0.5) * m,
      originZ: set.origin.z + (gpzMin + 0.5) * m,
      spacing: m,
      cols,
      rows,
      heights: filled,
      datum: 0,
    },
    pads,
  );
  const datum =
    Math.round(heightfieldHeightAt(rawPlan, datumPoint.x, datumPoint.z) * DATUM_QUANTUM) /
    DATUM_QUANTUM;
  const heightfield: HeightfieldPlan = {
    ...rawPlan,
    heights: rawPlan.heights.map((h) => h - datum),
    datum,
  };
  return { heightAt: (x, z) => heightfieldHeightAt(heightfield, x, z), heightfield };
}

/** Plain vertex/index arrays for a heightfield mesh (the builder wraps them in a BufferGeometry). */
export interface HeightfieldArrays {
  /** xyz per vertex, row-major (float32-exact: see the datum note). */
  positions: Float32Array;
  /** Two up-facing triangles per cell, split on the (i,j)–(i+1,j+1) diagonal. */
  indices: Uint32Array;
}

/** Triangle count of a heightfield mesh (for the tier budget). */
export function heightfieldTriangles(hf: HeightfieldPlan): number {
  return 2 * (hf.cols - 1) * (hf.rows - 1);
}

/** The heightfield's mesh arrays. Winding is counter-clockwise seen from +y (normals up). */
export function heightfieldArrays(hf: HeightfieldPlan): HeightfieldArrays {
  const positions = new Float32Array(hf.cols * hf.rows * 3);
  for (let j = 0; j < hf.rows; j += 1) {
    for (let i = 0; i < hf.cols; i += 1) {
      const k = (j * hf.cols + i) * 3;
      positions[k] = hf.originX + i * hf.spacing;
      positions[k + 1] = hf.heights[j * hf.cols + i] as number;
      positions[k + 2] = hf.originZ + j * hf.spacing;
    }
  }
  const indices = new Uint32Array(heightfieldTriangles(hf) * 3);
  let t = 0;
  for (let j = 0; j < hf.rows - 1; j += 1) {
    for (let i = 0; i < hf.cols - 1; i += 1) {
      const a = j * hf.cols + i; // (i, j)
      const b = a + 1; // (i+1, j)
      const d = a + hf.cols; // (i, j+1)
      const c = d + 1; // (i+1, j+1)
      // Triangle (i,j),(i+1,j+1),(i+1,j) — the fu ≥ fv half.
      indices[t++] = a;
      indices[t++] = c;
      indices[t++] = b;
      // Triangle (i,j),(i,j+1),(i+1,j+1) — the fu < fv half.
      indices[t++] = a;
      indices[t++] = d;
      indices[t++] = c;
    }
  }
  return { positions, indices };
}

/** The square grid a `GridHelper` would draw (the AD-315 legacy grid parameters). */
export interface DrapeGridSpec {
  /** Grid centre (world). */
  x: number;
  z: number;
  size: number;
  divisions: number;
  /** Lift above the surface (the legacy grid's `y`). */
  lift: number;
}

/** Line-segment arrays for a grid draped on a heightfield (xyz pairs + a per-vertex centre flag). */
export interface DrapedGridArrays {
  positions: Float32Array;
  /** 1 for vertices of the two centre lines (GridHelper's `colorCenter`), else 0. */
  centre: Uint8Array;
}

/**
 * The legacy AD-315 grid DRAPED on the heightfield (AD-408 §3 "the grid helper
 * is kept"): the same lines a `GridHelper(size, divisions)` draws, but each line
 * is broken at every cell edge AND every fixed-diagonal crossing, so every
 * segment lies inside ONE triangle and therefore exactly on the surface (plus
 * `lift`) — a chord across a fold would otherwise dip under the ground.
 */
export function drapedGridArrays(hf: HeightfieldPlan, grid: DrapeGridSpec): DrapedGridArrays {
  const half = grid.size / 2;
  const step = grid.size / grid.divisions;
  const out: number[] = [];
  const centre: number[] = [];
  const mid = grid.divisions / 2;
  const cellBreaks = (lo: number, hi: number, origin: number, count: number): number[] => {
    const breaks: number[] = [];
    for (let n = 0; n < count; n += 1) {
      const p = origin + n * hf.spacing;
      if (p > lo && p < hi) breaks.push(p);
    }
    return breaks;
  };
  for (let k = 0; k <= grid.divisions; k += 1) {
    const isCentre = k === mid ? 1 : 0;
    const c = -half + k * step;
    for (const axis of ['x', 'z'] as const) {
      // axis 'x': the line x = grid.x + c, running along z; 'z': z = grid.z + c, along x.
      const fixed = (axis === 'x' ? grid.x : grid.z) + c;
      const lo = (axis === 'x' ? grid.z : grid.x) - half;
      const hi = (axis === 'x' ? grid.z : grid.x) + half;
      const fixedOrigin = axis === 'x' ? hf.originX : hf.originZ;
      const runOrigin = axis === 'x' ? hf.originZ : hf.originX;
      const runCount = axis === 'x' ? hf.rows : hf.cols;
      const f = (fixed - fixedOrigin) / hf.spacing;
      const frac = f - Math.floor(f);
      const points = [lo, hi, ...cellBreaks(lo, hi, runOrigin, runCount)];
      // The fixed diagonal crosses this line at run-fraction == fixed-fraction in every cell.
      for (let n = -1; n < runCount; n += 1) {
        const p = runOrigin + (n + frac) * hf.spacing;
        if (p > lo && p < hi) points.push(p);
      }
      points.sort((a, b) => a - b);
      for (let s = 0; s + 1 < points.length; s += 1) {
        const a = points[s] as number;
        const b = points[s + 1] as number;
        if (b - a < 1e-9) continue;
        for (const run of [a, b]) {
          const x = axis === 'x' ? fixed : run;
          const z = axis === 'x' ? run : fixed;
          out.push(x, heightfieldHeightAt(hf, x, z) + grid.lift, z);
          centre.push(isCentre);
        }
      }
    }
  }
  return { positions: new Float32Array(out), centre: new Uint8Array(centre) };
}

/** Plain arrays for a heightfield cut to a polygon; normals are carried, never recomputed. */
export interface ClippedHeightfieldArrays {
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
}

type XZ = { x: number; z: number };

/** Twice the signed area of (a, b, c) in the x–z plane (negative ⇔ the mesh's up-facing winding). */
function cross2(a: XZ, b: XZ, c: XZ): number {
  return (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
}

/** Sutherland–Hodgman: convex `subject` ∩ convex `clip` (both in the x–z plane). */
function clipConvex(subject: readonly XZ[], clip: readonly XZ[]): XZ[] {
  const sense = Math.sign(cross2(clip[0] as XZ, clip[1] as XZ, clip[2] as XZ));
  let out: XZ[] = subject.slice();
  for (let e = 0; e < clip.length && out.length > 0; e += 1) {
    const p = clip[e] as XZ;
    const q = clip[(e + 1) % clip.length] as XZ;
    const side = (v: XZ): number => cross2(p, q, v) * sense;
    const input = out;
    out = [];
    for (let k = 0; k < input.length; k += 1) {
      const a = input[k] as XZ;
      const b = input[(k + 1) % input.length] as XZ;
      const sa = side(a);
      const sb = side(b);
      if (sa >= 0) out.push(a);
      if (sa >= 0 !== sb >= 0) {
        const t = sa / (sa - sb);
        out.push({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t });
      }
    }
  }
  return out;
}

/** Area-weighted vertex normals of the uncut heightfield mesh (three's `computeVertexNormals` law). */
function heightfieldVertexNormals(hf: HeightfieldPlan): Float64Array {
  const { cols, rows, spacing: s, heights: h } = hf;
  const n = new Float64Array(cols * rows * 3);
  const add = (k: number, x: number, y: number, z: number): void => {
    n[k * 3] = (n[k * 3] as number) + x;
    n[k * 3 + 1] = (n[k * 3 + 1] as number) + y;
    n[k * 3 + 2] = (n[k * 3 + 2] as number) + z;
  };
  // Face normal of (A, B, C) = (C − B) × (A − B), summed unnormalised into each corner.
  const face = (
    ka: number,
    kb: number,
    kc: number,
    ax: number,
    az: number,
    bx: number,
    bz: number,
    cx: number,
    cz: number,
  ): void => {
    const ya = h[ka] as number;
    const yb = h[kb] as number;
    const yc = h[kc] as number;
    const ux = (cx - bx) * s;
    const uy = yc - yb;
    const uz = (cz - bz) * s;
    const vx = (ax - bx) * s;
    const vy = ya - yb;
    const vz = (az - bz) * s;
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    add(ka, nx, ny, nz);
    add(kb, nx, ny, nz);
    add(kc, nx, ny, nz);
  };
  for (let j = 0; j < rows - 1; j += 1) {
    for (let i = 0; i < cols - 1; i += 1) {
      const a = j * cols + i;
      const b = a + 1;
      const d = a + cols;
      const c = d + 1;
      face(a, c, b, i, j, i + 1, j + 1, i + 1, j);
      face(a, d, c, i, j, i, j + 1, i + 1, j + 1);
    }
  }
  for (let k = 0; k < cols * rows; k += 1) {
    const x = n[k * 3] as number;
    const y = n[k * 3 + 1] as number;
    const z = n[k * 3 + 2] as number;
    const len = Math.sqrt(x * x + y * y + z * z) || 1;
    n[k * 3] = x / len;
    n[k * 3 + 1] = y / len;
    n[k * 3 + 2] = z / len;
  }
  return n;
}

/**
 * The heightfield CUT to a star-shaped polygon — the island top of a relief zone
 * (`q-island-edges-heightfield`, island-edges.md §8). The polygon is fanned from
 * `centre` (star-shaped ⇒ the fan tiles it exactly), each fan triangle is
 * intersected with every grid triangle it overlaps (convex ∩ convex), and each
 * piece is fanned again. Every piece lies inside ONE grid triangle and every
 * vertex takes that triangle's plane, so the cut surface IS the uncut one: the
 * seating law (`heightfieldHeightAt`) stays coplanar with what is drawn. Normals
 * are the uncut mesh's vertex normals interpolated barycentrically, so the
 * shading matches the rectangle's too. Winding: counter-clockwise from +y.
 */
export function heightfieldClippedArrays(
  hf: HeightfieldPlan,
  polygon: readonly XZ[],
  centre: XZ,
): ClippedHeightfieldArrays {
  const vn = heightfieldVertexNormals(hf);
  const { cols, rows, spacing: s, heights: h } = hf;
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  const emit = (p: XZ, i: number, j: number, upper: boolean): void => {
    const fu = (p.x - hf.originX) / s - i;
    const fv = (p.z - hf.originZ) / s - j;
    const k00 = j * cols + i;
    const k11 = k00 + cols + 1;
    // fu ≥ fv half: (i,j),(i+1,j),(i+1,j+1); fu < fv half: (i,j),(i,j+1),(i+1,j+1).
    const kMid = upper ? k00 + 1 : k00 + cols;
    const w00 = upper ? 1 - fu : 1 - fv;
    const wMid = upper ? fu - fv : fv - fu;
    const w11 = upper ? fv : fu;
    positions.push(
      p.x,
      (h[k00] as number) * w00 + (h[kMid] as number) * wMid + (h[k11] as number) * w11,
      p.z,
    );
    let nx = 0;
    let ny = 0;
    let nz = 0;
    for (const [k, w] of [
      [k00, w00],
      [kMid, wMid],
      [k11, w11],
    ] as const) {
      nx += (vn[k * 3] as number) * w;
      ny += (vn[k * 3 + 1] as number) * w;
      nz += (vn[k * 3 + 2] as number) * w;
    }
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    normals.push(nx / len, ny / len, nz / len);
  };
  const at = (i: number, j: number): XZ => ({ x: hf.originX + i * s, z: hf.originZ + j * s });
  for (let f = 0; f < polygon.length; f += 1) {
    const fan = [centre, polygon[f] as XZ, polygon[(f + 1) % polygon.length] as XZ];
    if (Math.abs(cross2(fan[0] as XZ, fan[1] as XZ, fan[2] as XZ)) < 1e-12) continue;
    const xs = fan.map((p) => (p.x - hf.originX) / s);
    const zs = fan.map((p) => (p.z - hf.originZ) / s);
    const i0 = Math.max(0, Math.floor(Math.min(...xs)));
    const i1 = Math.min(cols - 2, Math.floor(Math.max(...xs)));
    const j0 = Math.max(0, Math.floor(Math.min(...zs)));
    const j1 = Math.min(rows - 2, Math.floor(Math.max(...zs)));
    for (let j = j0; j <= j1; j += 1) {
      for (let i = i0; i <= i1; i += 1) {
        for (const upper of [true, false]) {
          const tri = upper
            ? [at(i, j), at(i + 1, j + 1), at(i + 1, j)]
            : [at(i, j), at(i, j + 1), at(i + 1, j + 1)];
          const piece = clipConvex(fan, tri);
          if (piece.length < 3) continue;
          let area = 0;
          for (let k = 1; k + 1 < piece.length; k += 1) {
            area += cross2(piece[0] as XZ, piece[k] as XZ, piece[k + 1] as XZ);
          }
          if (Math.abs(area) < 1e-6) continue;
          const base = positions.length / 3;
          for (const p of piece) emit(p, i, j, upper);
          for (let k = 1; k + 1 < piece.length; k += 1) {
            // Up-facing ⇔ negative x–z signed area (heightfieldArrays' winding).
            if (area < 0) indices.push(base, base + k, base + k + 1);
            else indices.push(base, base + k + 1, base + k);
          }
        }
      }
    }
  }
  return {
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    indices: Uint32Array.from(indices),
  };
}

/**
 * Seat a visual base y on the ground: `baseY + heightAt(x, z)`. The ONE seating
 * law every world-space category routes through (AD-408 §3 arm d; the §5.7
 * seat-completeness test greps for it). On flat ground `baseY` is returned
 * untouched — not `baseY + 0`, which would turn a `-0` into `+0` and break the
 * legacy byte-identity pins.
 */
export function seatY(ground: GroundSurface, x: number, z: number, baseY: number): number {
  return ground.heightfield === null ? baseY : baseY + ground.heightAt(x, z);
}
